/**
 * AdminRevisoesTab — Semáforo de next_review + notificações por e-mail.
 * Sprint 6: Extraído de AdminPanel.jsx.
 * Sprint 7: Automação — auto-define next_review para clientes sem data (90d),
 *           batch-send lembretes para vencidos/próximos, auto-reagendar após revisão.
 */
import { useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { Card, STitle, Badge, LIGHT, DARK } from "./adminUtils.jsx";
import { RADIUS } from "../shared/theme.js";
import EmptyState from "./components/EmptyState.jsx";
import { upsertClientMeta, logAudit } from "../supabaseClient.js";
import { logWarn } from "../shared/logger.js";

const MESES = {
  janeiro: 1,
  fevereiro: 2,
  março: 3,
  marco: 3,
  abril: 4,
  maio: 5,
  junho: 6,
  julho: 7,
  agosto: 8,
  setembro: 9,
  outubro: 10,
  novembro: 11,
  dezembro: 12,
};

const parseReviewDate = (text) => {
  if (!text) return null;
  const t = text.toLowerCase().trim();
  const m = t.match(/(\d{1,2})\s+de\s+(\w+)\s+de\s+(\d{4})/);
  if (!m) return null;
  const mes = MESES[m[2].normalize("NFD").replace(/[\u0300-\u036f]/g, "")];
  if (!mes) return null;
  return new Date(parseInt(m[3]), mes - 1, parseInt(m[1]));
};

/** Gera texto "15 de junho de 2026" para daqui a N dias */
function futureReviewText(days = 90) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toLocaleDateString("pt-BR", { day: "numeric", month: "long", year: "numeric" });
}

const semaforo = (days) => {
  if (days === null)
    return { cor: "#94A3B8", label: "Sem data", emoji: "⚫", status: "Sem data", shape: "●" };
  if (days < 0)
    return {
      cor: "#DC2626",
      label: `Vencida há ${Math.abs(days)}d`,
      emoji: "🔴",
      status: "Urgente",
      shape: "▲",
    };
  if (days === 0)
    return { cor: "#DC2626", label: "Hoje!", emoji: "🔴", status: "Urgente", shape: "▲" };
  if (days <= 60)
    return {
      cor: "#D97706",
      label: `${days}d restantes`,
      emoji: "🟡",
      status: "Atenção",
      shape: "◆",
    };
  return { cor: "#16A34A", label: `${days}d restantes`, emoji: "🟢", status: "Em dia", shape: "✓" };
};

export default function AdminRevisoesTab({
  dynamicClients,
  allClientsMeta,
  emailSending,
  handleSendEmail,
  setEditMeta,
  T,
}) {
  const [batchSending, setBatchSending] = useState(false);
  const [batchResult, setBatchResult] = useState(null);
  const [autoAssigning, setAutoAssigning] = useState(false);
  const [autoAssignResult, setAutoAssignResult] = useState(null);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const withDays = useMemo(
    () =>
      allClientsMeta
        .map((cm) => {
          const dt = parseReviewDate(cm.next_review);
          const diffDays = dt ? Math.round((dt - today) / 86400000) : null;
          const client = dynamicClients.find((c) => c.id === cm.client_id) || {
            name: cm.client_id,
          };
          return { ...cm, client, dt, diffDays };
        })
        .sort((a, b) => {
          if (a.diffDays === null && b.diffDays === null) return 0;
          if (a.diffDays === null) return 1;
          if (b.diffDays === null) return -1;
          return a.diffDays - b.diffDays;
        }),
    [allClientsMeta, dynamicClients, today]
  );

  const vencidas = useMemo(
    () => withDays.filter((c) => c.diffDays !== null && c.diffDays < 0),
    [withDays]
  );
  const proximas = useMemo(
    () => withDays.filter((c) => c.diffDays !== null && c.diffDays >= 0 && c.diffDays <= 60),
    [withDays]
  );
  const ok = useMemo(
    () => withDays.filter((c) => c.diffDays !== null && c.diffDays > 60),
    [withDays]
  );
  const semData = useMemo(() => withDays.filter((c) => c.diffDays === null), [withDays]);

  // ── AUTOMAÇÃO: Auto-definir next_review para clientes sem data ──────────
  const handleAutoAssignDates = useCallback(async () => {
    if (semData.length === 0) return;
    setAutoAssigning(true);
    const reviewText = futureReviewText(90);
    let assigned = 0;

    for (const cm of semData) {
      try {
        await upsertClientMeta(cm.client_id, { next_review: reviewText });
        assigned++;
      } catch (e) {
        logWarn("AdminRevisoesTab", e.message, { context: "auto-assign next_review" });
      }
    }

    setAutoAssignResult(`${assigned} cliente(s) agendados para ${reviewText}`);
    logAudit("revisoes_auto_assigned", { count: assigned, date: reviewText }).catch(() => {});
    setAutoAssigning(false);
  }, [semData]);

  // ── AUTOMAÇÃO: Batch-send lembretes para vencidos + próximos ────────────
  const handleBatchReminder = useCallback(async () => {
    const targets = [...vencidas, ...proximas].filter(
      (c) => c.client?.email || dynamicClients.find((dc) => dc.id === c.client_id)?.email
    );
    if (targets.length === 0) {
      setBatchResult("Nenhum cliente com e-mail na lista urgente/atenção.");
      return;
    }

    setBatchSending(true);
    let sent = 0;
    let failed = 0;

    for (const cm of targets) {
      try {
        await handleSendEmail(cm.client_id, null);
        sent++;
      } catch {
        failed++;
      }
    }

    const msg = `Enviados: ${sent} · Falhas: ${failed}`;
    setBatchResult(msg);
    logAudit("revisoes_batch_reminder", { sent, failed, total: targets.length }).catch(() => {});
    setBatchSending(false);
  }, [vencidas, proximas, dynamicClients, handleSendEmail]);

  // ── AUTOMAÇÃO: Reagendar vencidos para +90d ─────────────────────────────
  const handleReagendarVencidos = useCallback(async () => {
    if (vencidas.length === 0) return;
    setAutoAssigning(true);
    const reviewText = futureReviewText(90);
    let count = 0;

    for (const cm of vencidas) {
      try {
        await upsertClientMeta(cm.client_id, { next_review: reviewText });
        count++;
      } catch (e) {
        logWarn("AdminRevisoesTab", e.message, { context: "reagendar vencidos" });
      }
    }

    setAutoAssignResult(`${count} revisão(ões) vencida(s) reagendada(s) para ${reviewText}`);
    logAudit("revisoes_overdue_rescheduled", { count, date: reviewText }).catch(() => {});
    setAutoAssigning(false);
  }, [vencidas]);

  return (
    <div>
      {/* Auto-assign result */}
      {autoAssignResult && (
        <div
          style={{
            marginBottom: 16,
            padding: "10px 16px",
            background: T.successBg || "#DCFCE7",
            borderRadius: RADIUS.md,
            border: `1px solid ${T.success || "#16A34A"}30`,
            fontSize: 12,
            fontWeight: 600,
            color: T.success || "#16A34A",
          }}
        >
          ⚡ {autoAssignResult}
        </div>
      )}

      {/* Batch result */}
      {batchResult && (
        <div
          style={{
            marginBottom: 16,
            padding: "10px 16px",
            background: T.infoBg || "#DBEAFE",
            borderRadius: RADIUS.md,
            border: `1px solid ${T.info || "#2563EB"}30`,
            fontSize: 12,
            fontWeight: 600,
            color: T.info || "#2563EB",
          }}
        >
          📧 {batchResult}
        </div>
      )}

      {/* Cards de resumo + action buttons */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))",
          gap: 14,
          marginBottom: 20,
        }}
      >
        {[
          {
            label: "Revisões vencidas",
            v: vencidas.length,
            color: "#DC2626",
            bg: "#FEE2E2",
            emoji: "🔴",
          },
          {
            label: "Próximas 60 dias",
            v: proximas.length,
            color: "#D97706",
            bg: "#FEF3C7",
            emoji: "🟡",
          },
          { label: "Em dia", v: ok.length, color: "#16A34A", bg: "#DCFCE7", emoji: "🟢" },
          {
            label: "Sem data cadastrada",
            v: semData.length,
            color: "#94A3B8",
            bg: "#F1F5F9",
            emoji: "⚫",
          },
        ].map(({ label, v, color, bg, emoji }) => (
          <Card
            key={label}
            T={T}
            style={{ padding: "14px 16px", background: T === DARK ? undefined : bg }}
          >
            <div style={{ fontSize: 22, marginBottom: 4 }}>{emoji}</div>
            <div style={{ fontSize: 26, fontWeight: 800, color }}>{v}</div>
            <div style={{ fontSize: 11, color: T.sub, fontWeight: 600 }}>{label}</div>
          </Card>
        ))}
      </div>

      {/* Automation action bar */}
      <div
        style={{
          display: "flex",
          gap: 10,
          marginBottom: 18,
          flexWrap: "wrap",
        }}
      >
        {semData.length > 0 && (
          <button
            onClick={handleAutoAssignDates}
            disabled={autoAssigning}
            style={{
              padding: "8px 16px",
              borderRadius: RADIUS.sm,
              border: "none",
              background: "#7C3AED",
              color: "#fff",
              fontSize: 12,
              fontWeight: 700,
              cursor: autoAssigning ? "wait" : "pointer",
              opacity: autoAssigning ? 0.7 : 1,
            }}
          >
            {autoAssigning
              ? "⏳ Atribuindo..."
              : `📅 Auto-agendar ${semData.length} sem data (90d)`}
          </button>
        )}
        {vencidas.length > 0 && (
          <button
            onClick={handleReagendarVencidos}
            disabled={autoAssigning}
            style={{
              padding: "8px 16px",
              borderRadius: RADIUS.sm,
              border: "none",
              background: "#DC2626",
              color: "#fff",
              fontSize: 12,
              fontWeight: 700,
              cursor: autoAssigning ? "wait" : "pointer",
              opacity: autoAssigning ? 0.7 : 1,
            }}
          >
            {autoAssigning
              ? "⏳ Reagendando..."
              : `🔄 Reagendar ${vencidas.length} vencida(s) (+90d)`}
          </button>
        )}
        {(vencidas.length > 0 || proximas.length > 0) && (
          <button
            onClick={handleBatchReminder}
            disabled={batchSending || !!emailSending}
            style={{
              padding: "8px 16px",
              borderRadius: RADIUS.sm,
              border: "none",
              background: "#2563EB",
              color: "#fff",
              fontSize: 12,
              fontWeight: 700,
              cursor: batchSending ? "wait" : "pointer",
              opacity: batchSending ? 0.7 : 1,
            }}
          >
            {batchSending
              ? "⏳ Enviando..."
              : `✉️ Enviar lembrete em lote (${vencidas.length + proximas.length})`}
          </button>
        )}
      </div>

      {/* Tabela de revisões */}
      <Card T={T}>
        <STitle
          title="Agenda de Revisões por Cliente"
          sub="Baseado no campo next_review da tabela clients_meta · Semáforo: 🔴 vencida | 🟡 ≤60d | 🟢 >60d"
          T={T}
        />
        {allClientsMeta.length === 0 ? (
          <EmptyState
            icon="📅"
            title="Nenhum cliente com metadados cadastrados"
            subtitle="Cadastre clientes e defina datas de revisão para visualizar a agenda."
            T={T}
          />
        ) : (
          <div style={{ overflowX: "auto", marginTop: 14 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr>
                  {[
                    "Semáforo",
                    "Cliente",
                    "Código",
                    "Próxima Revisão",
                    "Status",
                    "Assessor",
                    "Ações",
                  ].map((h) => (
                    <th
                      key={h}
                      scope="col"
                      style={{
                        textAlign: "left",
                        padding: "9px 12px",
                        borderBottom: `2px solid ${T.border}`,
                        color: T.sub,
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h === "Semáforo" ? <span className="sr-only">{h}</span> : h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {withDays.map((cm, i) => {
                  const s = semaforo(cm.diffDays);
                  const isSending = emailSending === cm.client_id;
                  return (
                    <tr key={cm.client_id} style={{ background: i % 2 === 0 ? T.row1 : T.row2 }}>
                      <td style={{ padding: "10px 12px", fontSize: 16 }} aria-label={s.status}>
                        <span title={s.status}>{s.emoji}</span>
                        <span
                          style={{ fontSize: 10, fontWeight: 700, color: s.cor, marginLeft: 4 }}
                        >
                          {s.shape}
                        </span>
                      </td>
                      <td
                        style={{
                          padding: "10px 12px",
                          fontWeight: 700,
                          color: T.text,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {cm.client?.name || cm.client_id}
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <Badge text={cm.code || cm.client_id} color={T.info || "#2563EB"} />
                      </td>
                      <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                        <div style={{ fontWeight: 600, color: T.text, marginBottom: 2 }}>
                          {cm.next_review || "–"}
                        </div>
                        <div style={{ fontSize: 11, color: s.cor, fontWeight: 700 }}>{s.label}</div>
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <span
                          style={{
                            padding: "3px 10px",
                            borderRadius: RADIUS.full,
                            fontSize: 11,
                            fontWeight: 700,
                            background:
                              s.cor === "#16A34A"
                                ? "#DCFCE7"
                                : s.cor === "#D97706"
                                  ? "#FEF3C7"
                                  : s.cor === "#DC2626"
                                    ? "#FEE2E2"
                                    : "#F1F5F9",
                            color: s.cor,
                          }}
                        >
                          {s.status}
                        </span>
                      </td>
                      <td style={{ padding: "10px 12px", color: T.sub, fontSize: 12 }}>
                        {cm.advisor || "–"}
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button
                            onClick={() => handleSendEmail(cm.client_id, null)}
                            disabled={!!emailSending}
                            title={`Enviar lembrete de revisão para ${cm.client?.name || cm.client_id}`}
                            style={{
                              padding: "5px 10px",
                              borderRadius: RADIUS.sm,
                              border: "none",
                              background: isSending ? "#94A3B8" : "#2563EB",
                              color: "#fff",
                              fontSize: 11,
                              fontWeight: 700,
                              cursor: emailSending ? "wait" : "pointer",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {isSending ? "⏳…" : "✉️ Lembrete"}
                          </button>
                          <button
                            onClick={() => setEditMeta(cm.client_id)}
                            title="Editar data de revisão"
                            style={{
                              padding: "5px 10px",
                              borderRadius: RADIUS.sm,
                              border: "none",
                              background: "#7C3AED",
                              color: "#fff",
                              fontSize: 11,
                              fontWeight: 700,
                              cursor: "pointer",
                            }}
                          >
                            📅 Reagendar
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Callout */}
        <div
          style={{
            marginTop: 20,
            padding: "12px 16px",
            background: T === DARK ? "rgba(255,255,255,0.08)" : "#F0F9FF",
            borderRadius: RADIUS.md,
            border: `1px solid ${T === DARK ? "rgba(255,255,255,0.18)" : "#BAE6FD"}`,
          }}
        >
          <div
            style={{
              fontSize: 12,
              color: T === DARK ? "#7DD3FC" : "#0369A1",
              fontWeight: 600,
              marginBottom: 4,
            }}
          >
            ℹ️ Automação de Revisões
          </div>
          <div style={{ fontSize: 11, color: T === DARK ? "#BAE6FD" : "#0C4A6E", lineHeight: 1.6 }}>
            Clientes sem data de revisão podem ser agendados automaticamente com o botão{" "}
            <strong>📅 Auto-agendar</strong> (padrão: +90 dias). Revisões vencidas podem ser
            reagendadas em lote com <strong>🔄 Reagendar vencidas</strong>. Use{" "}
            <strong>✉️ Enviar lembrete em lote</strong> para notificar todos os clientes com revisão
            urgente ou próxima.
          </div>
        </div>
      </Card>
    </div>
  );
}

AdminRevisoesTab.propTypes = {
  dynamicClients: PropTypes.array.isRequired,
  allClientsMeta: PropTypes.array.isRequired,
  emailSending: PropTypes.string,
  handleSendEmail: PropTypes.func.isRequired,
  setEditMeta: PropTypes.func.isRequired,
  T: PropTypes.object.isRequired,
};
